import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Non-standard Chromium event. Safari and Firefox don't fire this,
 * so `canInstall` stays `false` and the install button never renders.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * React hook that wraps the `beforeinstallprompt` browser event.
 *
 * Usage:
 *
 *   const { canInstall, install } = useInstallPrompt();
 *   return canInstall ? <button onClick={install}>Install app</button> : null;
 *
 * - `canInstall` is `true` only in Chromium-based browsers that have
 *   not yet installed the PWA and have seen the manifest pass the
 *   installability check.
 * - `install()` shows the native install dialog; once the user
 *   accepts or dismisses it, `canInstall` resets to `false`.
 */
export function useInstallPrompt(): { canInstall: boolean; install: () => Promise<void> } {
  const [canInstall, setCanInstall] = useState(false);
  const deferredRef = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const handler = (event: Event): void => {
      event.preventDefault();
      deferredRef.current = event as BeforeInstallPromptEvent;
      setCanInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const install = useCallback(async (): Promise<void> => {
    const prompt = deferredRef.current;
    if (!prompt) return;
    await prompt.prompt();
    deferredRef.current = null;
    setCanInstall(false);
  }, []);

  return { canInstall, install };
}
